import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../../context/LangContext";
import { useCMS } from "../../context/CMSContext";
import { SITE_CONFIG } from "../../config/siteConfig";
import { useInView } from "../../hooks/useInView";
import SectionHeader from "../common/SectionHeader";
import { CheckCircle2 } from "../common/Icon";
import { colors, fonts } from "../../styles/tokens";
import styles from "./Pricing.module.css";

function Pricing() {
  const { t, lang = "en" } = useLang();
  const { cms } = useCMS();
  const [ref, visible] = useInView();

  // ── Data from CMS; fall back to static translations ────────────────────────
  const cmsPlans = cms?.pricing?.plans;

  const prices =
    cmsPlans && cmsPlans.length > 0
      ? cmsPlans.map((p) => {
          const sym =
            p.currency === "TRY" ? "₺" : p.currency === "USD" ? "$" : "€";
          return p.interval === "free"
            ? lang === "tr"
              ? "Ücretsiz"
              : "Free"
            : `${sym}${p.price}`;
        })
      : [SITE_CONFIG.pricing.regularPrice, SITE_CONFIG.pricing.proPrice];

  const plans =
    cmsPlans && cmsPlans.length > 0
      ? cmsPlans.map((p, i) => ({
          name: p.name,
          description:
            p.interval === "free"
              ? lang === "tr"
                ? "Temel özelliklerle ücretsiz başlayın"
                : "Start free with the essentials"
              : lang === "tr"
                ? "Tüm özelliklerle maksimum etki"
                : "Maximum impact with all features",
          badge: p.highlighted
            ? lang === "tr"
              ? "EN POPÜLER"
              : "MOST POPULAR"
            : null,
          features: p.features ? p.features.split("\n").filter(Boolean) : [],
          cta: p[`cta_${lang}`] || p.cta_en || t.pricing.getStarted,
        }))
      : t.pricing.plans.map((p) => ({ ...p, cta: t.pricing.getStarted }));

  return (
    <section id="pricing" className={styles.section} ref={ref}>
      <div className="container">
        <SectionHeader
          label={t.pricing.label}
          heading={t.pricing.heading}
          maxWidth={480}
        />
        <div className={styles.trialBadge}>{t.pricing.trialNote}</div>
        <div className={styles.grid}>
          {plans.map((plan, i) => {
            const isPro = cmsPlans
              ? (cmsPlans[i]?.highlighted ?? false)
              : i === 1;

            return (
              <div
                key={i}
                className={`${styles.card} ${isPro ? styles.proCard : ""} ${visible ? styles.visible : ""}`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                {plan.badge && <div className={styles.badge}>{plan.badge}</div>}

                <h3
                  style={{
                    fontFamily: fonts.display,
                    fontSize: 20,
                    fontWeight: 700,
                    color: isPro ? "#fff" : colors.text,
                    margin: "0 0 4px",
                  }}
                >
                  {plan.name}
                </h3>

                <p
                  style={{
                    fontFamily: fonts.body,
                    fontSize: 14,
                    color: isPro ? "rgba(255,255,255,0.5)" : colors.grayLight,
                    margin: "0 0 24px",
                  }}
                >
                  {plan.description}
                </p>

                <div className={styles.priceRow}>
                  <span
                    style={{
                      fontFamily: fonts.display,
                      fontSize: 44,
                      fontWeight: 900,
                      color: isPro ? "#fff" : colors.text,
                    }}
                  >
                    {prices[i]}
                  </span>
                  {/* Only show /month for paid plans */}
                  {cmsPlans ? (
                    cmsPlans[i]?.interval !== "free" && (
                      <span
                        style={{
                          fontFamily: fonts.body,
                          fontSize: 15,
                          color: isPro
                            ? "rgba(255,255,255,0.4)"
                            : colors.grayLight,
                        }}
                      >
                        {t.pricing.perMonth}
                      </span>
                    )
                  ) : (
                    <span
                      style={{
                        fontFamily: fonts.body,
                        fontSize: 15,
                        color: isPro
                          ? "rgba(255,255,255,0.4)"
                          : colors.grayLight,
                      }}
                    >
                      {t.pricing.perMonth}
                    </span>
                  )}
                </div>

                <ul className={styles.featureList}>
                  {plan.features.map((f, j) => (
                    <li key={j} className={styles.featureItem}>
                      <CheckCircle2
                        size={17}
                        color={isPro ? colors.red : colors.blue}
                        strokeWidth={2}
                      />
                      <span
                        style={{
                          fontFamily: fonts.body,
                          fontSize: 14,
                          color: isPro ? "rgba(255,255,255,0.72)" : colors.gray,
                        }}
                      >
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/signup"
                  className={`${styles.ctaBtn} ${isPro ? styles.ctaBtnPro : styles.ctaBtnRegular}`}
                >
                  {plan.cta}
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default Pricing;
